import type { ConversationSummary } from "../types"
import type { MessageApiService } from "./api-service"

/**
 * Coalesces per-row summary requests into one call per entity type.
 *
 * The problem it solves: a Workflow Hub grid page mounts 25-100 chat triggers at once, and each of
 * them independently wanting its own badge state is an N+1 — the exact cost `POST
 * /api/conversations/summaries` exists to remove. Batching here rather than at the call site means
 * every consumer gets it without knowing anything about it, and a grid does not have to hoist message
 * state up to the page just to fetch it in bulk.
 *
 * Requests that land within {@link FLUSH_DELAY_MS} of each other go out together. That window is short
 * enough to be invisible and long enough to catch a whole page of rows mounting in one React commit.
 */

const FLUSH_DELAY_MS = 25

/**
 * Must not exceed the server's per-call cap, or the request comes back 400. Larger pages are split
 * into several calls rather than truncated — a silently dropped row would render as "no messages".
 */
const MAX_ENTITY_IDS_PER_CALL = 200

interface Waiter {
  resolve: (summary: ConversationSummary | null) => void
  reject: (error: unknown) => void
}

class ConversationSummaryBatcher {
  private api: MessageApiService | null = null
  private pending = new Map<string, Map<string, Waiter[]>>()
  private timer: ReturnType<typeof setTimeout> | null = null

  /**
   * Always take the freshest service instance. The token getter is captured inside it, so holding on
   * to the first one would keep using a token that has since been refreshed.
   */
  setApi(api: MessageApiService): void {
    this.api = api
  }

  /**
   * Resolves with the entity's summary, or `null` when the entity has no conversation and no messages.
   * `null` is a real answer — "nothing to show" — not an error.
   */
  fetch(entityTypeId: string, entityId: string): Promise<ConversationSummary | null> {
    return new Promise((resolve, reject) => {
      let byEntityId = this.pending.get(entityTypeId)
      if (!byEntityId) {
        byEntityId = new Map()
        this.pending.set(entityTypeId, byEntityId)
      }

      const waiters = byEntityId.get(entityId)
      if (waiters) {
        // Two rows asking about the same entity share one slot in the request.
        waiters.push({ resolve, reject })
      } else {
        byEntityId.set(entityId, [{ resolve, reject }])
      }

      this.schedule()
    })
  }

  private schedule(): void {
    if (this.timer !== null) return
    this.timer = setTimeout(() => {
      this.timer = null
      void this.flush()
    }, FLUSH_DELAY_MS)
  }

  private async flush(): Promise<void> {
    const api = this.api
    // Swap the queue out before any await, so requests arriving mid-flight start a fresh batch
    // instead of being resolved by a call that never asked about them.
    const pending = this.pending
    this.pending = new Map()

    if (!api) {
      pending.forEach((byEntityId) =>
        byEntityId.forEach((waiters) => waiters.forEach((waiter) => waiter.resolve(null)))
      )
      return
    }

    for (const [entityTypeId, byEntityId] of pending) {
      const entityIds = Array.from(byEntityId.keys())

      for (let offset = 0; offset < entityIds.length; offset += MAX_ENTITY_IDS_PER_CALL) {
        const chunk = entityIds.slice(offset, offset + MAX_ENTITY_IDS_PER_CALL)

        try {
          const summaries = await api.fetchConversationSummaries({ entityTypeId, entityIds: chunk })
          const byId = new Map(summaries.map((summary) => [summary.entityId, summary]))

          chunk.forEach((entityId) => {
            // Absent from the response means no conversation and no messages.
            const summary = byId.get(entityId) ?? null
            byEntityId.get(entityId)?.forEach((waiter) => waiter.resolve(summary))
          })
        } catch (error) {
          chunk.forEach((entityId) => {
            byEntityId.get(entityId)?.forEach((waiter) => waiter.reject(error))
          })
        }
      }
    }
  }
}

/**
 * Change notification, so a bulk action does not have to reload the page.
 *
 * A bulk mark read/unread changes N rows, each of which owns its own `useConversationSummary`. This
 * lets the caller say "these entities moved" and have every mounted hook for them re-read. Because
 * the batcher above coalesces, N notified hooks collapse into ONE `summaries` request — the same
 * mechanism that removed the per-row N+1 in the first place.
 *
 * Module-global rather than per-endpoint: a listener only ever reacts to its own
 * `(entityTypeId, entityId)`, so there is nothing for a second endpoint to collide with.
 */
export type ConversationChangeListener = (entityTypeId: string, entityId: string) => void

const changeListeners = new Set<ConversationChangeListener>()

export function subscribeToConversationChanges(
  listener: ConversationChangeListener
): () => void {
  changeListeners.add(listener)
  return () => {
    changeListeners.delete(listener)
  }
}

export function notifyConversationsChanged(entityTypeId: string, entityIds: string[]): void {
  if (!entityTypeId || entityIds.length === 0) return

  entityIds.forEach((entityId) => {
    changeListeners.forEach((listener) => {
      try {
        listener(entityTypeId, entityId)
      } catch {
        // One misbehaving subscriber must not stop the others being told.
      }
    })
  })
}

/**
 * One batcher per endpoint, not per component and not per API-service instance: `useMessageApi`
 * re-memoizes whenever the provider config identity changes, and a batcher that changed with it would
 * coalesce nothing. Keyed on the endpoint string so it survives that.
 */
const batchers = new Map<string, ConversationSummaryBatcher>()

export function getConversationSummaryBatcher(
  apiEndpoint: string,
  api: MessageApiService
): ConversationSummaryBatcher {
  let batcher = batchers.get(apiEndpoint)
  if (!batcher) {
    batcher = new ConversationSummaryBatcher()
    batchers.set(apiEndpoint, batcher)
  }
  batcher.setApi(api)
  return batcher
}

export type { ConversationSummaryBatcher }
